// src/api/tasks.ts
import api from "./api";

// Types
export interface TaskCodeSettings {
  id: string;
  prefix: string;
  suffix: string;
  padding: number;
  start_number: number;
  next_number: number;
  // Live preview of the next task's code — used as the read-only "Next
  // Task ID" field on the Create Task form.
  next_preview: string;
  updated_at: string | null;
}

// Per-branch email-notification toggles (admin). Gate the EMAIL channel only;
// in-app bell + push are never affected. A null payload from the API means the
// branch has never configured prefs → treat every switch as ON.
export interface TaskNotificationSettings {
  id: string;
  email_enabled: boolean;
  email_on_task: boolean;
  email_on_planner: boolean;
  updated_at: string | null;
}

export interface Task {
  id: string;
  // Auto-generated, human-readable code like "TASK-0001". null on tasks
  // created before the series was configured.
  code: string | null;
  title: string;
  description: string | null;
  task_type: string;
  tags: string[];
  status: string;
  priority: string;
  created_by: string;
  created_by_name?: string | null;
  primary_assignee_id: string | null;
  primary_assignee_name?: string | null;
  department_id: string | null;
  client_id?: string | null;
  client_name?: string | null;
  vendor_id?: string | null;
  vendor_name?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  // The project's own human-readable code (e.g. "WEB-02"). Shown alongside
  // task.code on the card so a viewer can see both at a glance.
  project_code?: string | null;
  parent_task_id: string | null;
  start_date: string | null;
  due_date: string | null;
  completed_at: string | null;
  estimated_hours: number | null;
  actual_hours: number;
  progress_percent: number;
  blocked_reason: string | null;
  is_overdue: boolean;
  is_recurring: boolean;
  recurrence_rule: any;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
  headOfficeID: string | null;
  branchID: string | null;
  // Returned only when a task's creator is viewing a cross-org draft from
  // outside the receiving organization.
  is_originator_view?: boolean;
}

export interface TaskAssignee {
  id: string;
  task_id: string;
  user_id: string;
  employee_name?: string;
  employee_id: string | null;
  role_in_task: string;
  assigned_by: string;
  assigned_at: string;
  accepted_at: string | null;
  removed_at: string | null;
  is_active: boolean;
  progress_percent: number;
}

export interface TaskComment {
  id: string;
  task_id: string;
  user_id: string;
  commenter_name?: string;
  comment: string;
  mentioned_user_ids: string[];
  parent_comment_id: string | null;
  edited_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TaskAttachment {
  id: string;
  task_id: string;
  uploaded_by: string;
  uploaded_by_name?: string | null;
  file_path: string;
  file_url: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  is_active: boolean;
  created_at: string;
}

export interface TaskChecklistItem {
  id: string;
  task_id: string;
  item: string;
  order_index: number;
  is_done: boolean;
  done_by: string | null;
  done_by_name?: string | null;
  done_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface TaskActivity {
  id: string;
  task_id: string;
  actor_user_id: string;
  actor_name?: string | null;
  action: string;
  from_value: string | null;
  to_value: string | null;
  message: string | null;
  meta: any;
  created_at: string;
}

export interface TaskNotification {
  id: string;
  task_id: string;
  user_id: string;
  kind: string;
  title: string;
  body: string;
  is_read: boolean;
  read_at: string | null;
  email_sent: boolean;
  email_sent_at: string | null;
  created_at: string;
}

export interface AssigneeIn {
  user_id: string;
  employee_id?: string;
  role_in_task: 'PREPAROR' | 'OWNER' | 'COLLABORATOR' | 'REVIEWER';
}

export interface TaskCreatePayload {
  title: string;
  description?: string;
  task_type?: string;
  tags?: string[];
  priority?: string;
  status?: string;
  department_id?: string;
  parent_task_id?: string;
  start_date?: string;
  due_date?: string;
  estimated_hours?: number;
  assignees?: AssigneeIn[];
  checklist?: string[];
  is_recurring?: boolean;
  recurrence_rule?: any;
  client_id?: string | null;
  vendor_id?: string | null;
  project_id?: string | null;
  extra_group_member_ids?: string[];
}

export interface TaskUpdatePayload {
  title?: string;
  description?: string;
  task_type?: string;
  tags?: string[];
  priority?: string;
  department_id?: string;
  client_id?: string | null;
  vendor_id?: string | null;
  project_id?: string | null;
  start_date?: string;
  due_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
  progress_percent?: number;
  assignees?: AssigneeIn[];
  replace_assignees?: boolean;
  checklist?: string[];
  replace_checklist?: boolean;
  is_recurring?: boolean;
  recurrence_rule?: any;
}

export interface TaskStatusChangePayload {
  status: string;
  blocked_reason?: string;
}

export interface TaskProgressUpdatePayload {
  progress_percent: number;
  actual_hours?: number;
  note?: string;
}

export interface TaskAssignPayload {
  assignees: AssigneeIn[];
  replace: boolean;
}

export interface TaskReassignPayload {
  from_user_id: string;
  to_user_id: string;
  to_employee_id?: string;
  note?: string;
}

export interface BulkReassignPayload {
  from_user_id: string;
  to_user_id: string;
  to_employee_id?: string;
  only_open: boolean;
  note?: string;
}

export interface TaskCommentCreatePayload {
  comment: string;
  mentioned_user_ids?: string[];
  parent_comment_id?: string;
}

export interface TaskCommentUpdatePayload {
  comment: string;
}

export interface ChecklistItemCreatePayload {
  item: string;
  order_index?: number;
}

export interface ChecklistItemUpdatePayload {
  item?: string;
  order_index?: number;
  is_done?: boolean;
}

export interface TaskFilters {
  status?: string;
  priority?: string;
  assigned_to?: string;
  created_by?: string;
  department_id?: string;
  parent_task_id?: string;
  tag?: string;
  search?: string;
  overdue?: boolean;
  due_before?: string;
  due_after?: string;
  include_closed?: boolean;
  client_id?: string;
  vendor_id?: string;
  project_id?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface TaskSettings {
  include_closed_tasks: boolean;
  created_by_me: boolean;
}

export interface TaskListResponse {
  total: number;
  page: number;
  page_size: number;
  items: Task[];
  status_counts?: Record<string, number>;
  overdue_count?: number;
  all_count?: number;
}

export interface TaskDetailResponse {
  task: Task;
  assignees: TaskAssignee[];
  comments: TaskComment[];
  attachments: TaskAttachment[];
  checklist: TaskChecklistItem[];
  activity: TaskActivity[];
}

export interface DashboardStats {
  open: number;
  overdue: number;
  due_today: number;
  unassigned: number;
  blocked: number;
  stale_over_7_days: number;
  done_this_month: number;
  by_priority: Record<string, number>;
}

// Current user's per-day task flow for the current week (Mon→Sun).
export interface WeeklyTaskFlow {
  week_start: string; // YYYY-MM-DD (Monday)
  days: { date: string; assigned: number; completed: number }[];
}

// Organization-wide weekly task flow (admin): per-day tasks CREATED vs COMPLETED
// across every task the caller can see (scoped by the backend's visible_task_where).
export interface OrgWeeklyTaskFlow {
  week_start: string; // YYYY-MM-DD (Monday)
  days: { date: string; created: number; completed: number }[];
}

// Current user's monthly task productivity. `productivity` is the percentage
// (tasks completed or reviewed / total tasks created for the user) * 100; the
// status counts power the pie chart's side legend.
export interface MonthlyProductivity {
  month: string; // YYYY-MM
  total: number; // tasks created for the user this month
  todo: number;
  in_progress: number;
  completed: number;
  reviewed: number;
  productivity: number; // 0-100
}


// Task Type Types
export interface TaskType {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateTaskTypePayload {
  name: string;
}

export interface EmployeeStats {
  user_id: string;
  open: number;
  overdue: number;
  completed_this_month: number;
  blocked: number;
  by_priority: Record<string, number>;
}

export interface TaskWorkloadPreview {
  employee_id: string;
  date: string;
  office_hours: number;
  assigned_hours: number;
  remaining_hours: number;
  non_working_reason?: string | null;
}

export interface TaskWorkloadPreviewParams {
  employee_id: string;
  date: string;
  exclude_task_id?: string | null;
}

// Task API functions (matching your pattern - no Promise wrapper)
export const tasksApi = {
  // Create task
  create: (payload: TaskCreatePayload) => {
    return api.post(`/tasks`, payload);
  },

  // ── Task Code Series settings (per-branch) ──
  // GET returns { settings: TaskCodeSettings | null }
  getCodeSettings: () => api.get(`/tasks/code-settings`),
  // Upsert. Pass only the fields you want to change.
  saveCodeSettings: (payload: {
    prefix?: string;
    suffix?: string;
    padding?: number;
    start_number?: number;
  }) => api.post(`/tasks/code-settings`, payload),

  // ── Task email-notification toggles (per-branch, admin) ──
  // GET returns { settings: TaskNotificationSettings | null } (null == all on)
  getNotificationSettings: () => api.get(`/tasks/notification-settings`),
  // Upsert. Pass only the fields you want to change.
  saveNotificationSettings: (payload: {
    email_enabled?: boolean;
    email_on_task?: boolean;
    email_on_planner?: boolean;
  }) => api.post(`/tasks/notification-settings`, payload),

  // Create a cross-org draft task (lands in target org as status=DRAFT)
  createCrossOrgDraft: (
    payload: TaskCreatePayload & { target_head_office_id: string; is_cross_org_draft?: boolean },
  ) => {
    return api.post(`/tasks/cross-org-draft`, payload);
  },

  // Promote a DRAFT task to active (admin sets assignees + approval chain)
  promoteDraft: (taskId: string, payload: {
    assignees: Array<{ user_id: string; role_in_task: string; employee_id?: string }>;
    approval_chain?: Array<{ level: number; approver: string; role_label?: string }>;
    priority?: string;
    start_date?: string | null;
    due_date?: string | null;
    estimated_hours?: number;
  }) => {
    return api.post(`/tasks/${taskId}/promote-draft`, payload);
  },

  // Send a draft back for changes (status stays DRAFT)
  sendBackDraft: (taskId: string, reason: string) => {
    return api.post(`/tasks/${taskId}/send-back`, { reason });
  },

  // Reject a draft (status flips to CANCELLED)
  rejectDraft: (taskId: string, reason: string) => {
    return api.post(`/tasks/${taskId}/reject-draft`, { reason });
  },

  // List cross-org tasks the caller submitted into other tenancies, with the
  // receiving org's name and current status. Powers the "Sent Drafts" tab.
  listSentDrafts: () => {
    return api.get(`/tasks/sent-drafts`);
  },

  // List tasks the caller created with a client attached. Used in the Client
  // sub-tab to merge "my tasks for a client" alongside incoming cross-org drafts.
  listTasksForClientsIManage: () => {
    return api.get(`/tasks/for-clients-i-manage`);
  },

  // Client portal endpoints — caller is the ClientDetails.linked_user_id.
  // Returns a stripped projection (no assignees / preparer / approval chain).
  listTasksForMeAsClient: () => {
    return api.get(`/tasks/for-me-as-client`);
  },
  getTaskClientView: (taskId: string) => {
    return api.get(`/tasks/${taskId}/client-view`);
  },
  postClientComment: (taskId: string, comment: string) => {
    return api.post(`/tasks/${taskId}/client-comment`, { comment });
  },
  postClientAttachment: (taskId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tasks/${taskId}/client-attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Originator may edit their own task while it's still a DRAFT.
  updateMyDraft: (taskId: string, payload: {
    title?: string;
    description?: string | null;
    priority?: string;
    due_date?: string | null;
  }) => {
    return api.patch(`/tasks/${taskId}/my-draft`, payload);
  },
  // Per-user archive: hides the task from MY partner views. Idempotent.
  archiveTaskForMe: (taskId: string) => api.post(`/tasks/${taskId}/archive`),
  unarchiveTaskForMe: (taskId: string) => api.delete(`/tasks/${taskId}/archive`),
  // Originator soft-deletes their own cross-org task. Goes to Recently Deleted.
  deleteMyPartnerTask: (taskId: string) => api.delete(`/tasks/${taskId}/my-task`),

  // List draft tasks for admin review
  listDrafts: (countOnly = false) => {
    return api.get(`/tasks/drafts${countOnly ? '?count_only=true' : ''}`);
  },

  // List tasks originated by current user in a target org (for client/vendor Tasks tab)
  listOriginatedByMe: (targetHeadOfficeId: string) => {
    return api.get(`/tasks/originated-by-me?target_head_office_id=${encodeURIComponent(targetHeadOfficeId)}`);
  },

  // List tasks with filters
  list: (filters: TaskFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    const queryString = params.toString();
    return api.get(`/tasks${queryString ? `?${queryString}` : ''}`);
  },

  // Get my tasks
  getMyTasks: (includeClosed = true) => {
    return api.get(`/tasks/my?include_closed=${includeClosed}`);
  },

  // Get team tasks
  getTeamTasks: (includeClosed = true) => {
    return api.get(`/tasks/team?include_closed=${includeClosed}`);
  },

  // Get single task with details
  get: (taskId: string) => {
    return api.get(`/tasks/${taskId}`);
  },

  getWorkloadPreview: (params: TaskWorkloadPreviewParams) => {
    return api.get<TaskWorkloadPreview>(`/tasks/workload-preview`, { params });
  },

  // Update task
  update: (taskId: string, payload: TaskUpdatePayload) => {
    return api.patch(`/tasks/${taskId}`, payload);
  },

  // Delete task (soft delete)
  delete: (taskId: string) => {
    return api.delete(`/tasks/${taskId}`);
  },

  // Recently deleted (trash) — list, restore, hard-delete
  listRecentlyDeleted: (params: { days?: number; page?: number; page_size?: number } = {}) => {
    return api.get(`/tasks/recently-deleted`, { params });
  },
  restore: (taskId: string) => {
    return api.post(`/tasks/${taskId}/restore`);
  },
  permanentDelete: (taskId: string) => {
    return api.delete(`/tasks/${taskId}/permanent`);
  },

  // Change status
  changeStatus: (taskId: string, payload: TaskStatusChangePayload) => {
    return api.post(`/tasks/${taskId}/status`, payload);
  },

  // Reopen a closed task (DONE/CANCELLED → TODO)
  reopen: (taskId: string) => {
    return api.post(`/tasks/${taskId}/reopen`);
  },

  // Send task for review (preparor action)
  sendForReview: (taskId: string) => {
    return api.post(`/tasks/${taskId}/send-for-review`, {});
  },

  // Update progress
  updateProgress: (taskId: string, payload: TaskProgressUpdatePayload) => {
    return api.post(`/tasks/${taskId}/progress`, payload);
  },

  // Assign users
  assignUsers: (taskId: string, payload: TaskAssignPayload) => {
    return api.post(`/tasks/${taskId}/assign`, payload);
  },

  // Remove assignee
  removeAssignee: (taskId: string, userId: string) => {
    return api.delete(`/tasks/${taskId}/assignees`, { data: { user_id: userId } });
  },

  // Reassign task
  reassign: (taskId: string, payload: TaskReassignPayload) => {
    return api.post(`/tasks/${taskId}/reassign`, payload);
  },

  // Bulk reassign
  bulkReassign: (payload: BulkReassignPayload) => {
    return api.post(`/tasks/bulk_reassign`, payload);
  },

  // Comments
  addComment: (taskId: string, payload: TaskCommentCreatePayload) => {
    return api.post(`/tasks/${taskId}/comments`, payload);
  },

  getComments: (taskId: string) => {
    return api.get(`/tasks/${taskId}/comments`);
  },

  updateComment: (commentId: string, payload: TaskCommentUpdatePayload) => {
    return api.put(`/tasks/comments/${commentId}`, payload);
  },

  deleteComment: (commentId: string) => {
    return api.delete(`/tasks/comments/${commentId}`);
  },


   uploadAttachment: (taskId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post(`/tasks/${taskId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: [(data) => data],
    });
  },

  getAttachments: (taskId: string) => {
    return api.get(`/tasks/${taskId}/attachments`);
  },

  deleteAttachment: (attachmentId: string) => {
    return api.delete(`/tasks/attachments/${attachmentId}`);
  },

  // Checklist
  addChecklistItem: (taskId: string, payload: ChecklistItemCreatePayload) => {
    return api.post(`/tasks/${taskId}/checklist`, payload);
  },

  updateChecklistItem: (itemId: string, payload: ChecklistItemUpdatePayload) => {
    return api.patch(`/tasks/checklist/${itemId}`, payload);
  },

  deleteChecklistItem: (itemId: string) => {
    return api.delete(`/tasks/checklist/${itemId}`);
  },

  // Activity
  getActivity: (taskId: string, limit = 200) => {
    return api.get(`/tasks/${taskId}/activity?limit=${limit}`);
  },

  // Stats
  getDashboardStats: () => {
    return api.get(`/tasks/stats/dashboard`);
  },

  getEmployeeStats: (userId: string) => {
    return api.get(`/tasks/stats/employee/${userId}`);
  },

  // Employee dashboard charts (current user)
  getMyWeeklyFlow: () => {
    return api.get<WeeklyTaskFlow>(`/tasks/stats/my-weekly-flow`);
  },

  getMyMonthlyProductivity: () => {
    return api.get<MonthlyProductivity>(`/tasks/stats/my-monthly-productivity`);
  },

  // Organization-wide dashboard charts (admin / elevated). Both are scoped
  // server-side by visible_task_where, so they respect the caller's role/access.
  getOrgWeeklyFlow: () => {
    return api.get<OrgWeeklyTaskFlow>(`/tasks/stats/org-weekly-flow`);
  },

  getOrgMonthlyProductivity: () => {
    return api.get<MonthlyProductivity>(`/tasks/stats/org-monthly-productivity`);
  },

  // Notifications
  getNotifications: (unreadOnly = true, limit = 50) => {
    return api.get(`/tasks/notifications/my?unread_only=${unreadOnly}&limit=${limit}`);
  },

  markNotificationRead: (notificationId: string) => {
    return api.post(`/tasks/notifications/${notificationId}/read`);
  },

  markAllNotificationsRead: () => {
    return api.post(`/tasks/notifications/read_all`);
  },

  deleteNotification: (notificationId: string) => {
    return api.delete(`/tasks/notifications/${notificationId}`);
  },
  deleteAllNotifications: () => {
    return api.delete(`/tasks/notifications/all`);
  },

  // Per-task mute (silences toast/badge; row still stored)
  getMutedTasks: () => {
    return api.get<{ task_ids: string[] }>(`/tasks/notifications/muted`);
  },
  getTaskMute: (taskId: string) => {
    return api.get<{ muted: boolean }>(`/tasks/${taskId}/mute`);
  },
  toggleTaskMute: (taskId: string) => {
    return api.post<{ muted: boolean }>(`/tasks/${taskId}/mute`);
  },

  // Per-user task settings (Filters-card checkboxes)
  getTaskSettings: () => {
    return api.get<TaskSettings>(`/tasks/settings`);
  },
  saveTaskSettings: (payload: Partial<TaskSettings>) => {
    return api.put<TaskSettings>(`/tasks/settings`, payload);
  },
 
   // NEW: Task Type Management APIs
  createTaskType: (payload: CreateTaskTypePayload) => {
    return api.post<TaskType>(`/tasks/types`, payload);
  },

  // getTaskTypes: () => {
  //   return api.get<TaskType[]>(`/tasks/types`);
  // },

  getTaskTypes: () => {
  return api.get<{
    total: number;
    items: TaskType[];
  }>(`/tasks/types`);
},

  getTaskType: (typeId: string) => {
    return api.get<TaskType>(`/tasks/types/${typeId}`);
  },

  updateTaskType: (typeId: string, payload: CreateTaskTypePayload) => {
    return api.put<TaskType>(`/tasks/types/${typeId}`, payload);
  },

  deleteTaskType: (typeId: string) => {
    return api.delete(`/tasks/types/${typeId}`);
  },

};

export default tasksApi;
